import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Send, MessageSquare, Users, Loader2, CheckCircle,
  AlertCircle, Link2, Hash, Radio
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function BulkDMProgress({ progress, onDismiss }) {
  if (!progress || (!progress.running && !progress.status)) return null;
  const done = progress.status === "completed";
  const isErr = progress.status?.startsWith("error");
  const pct = progress.total > 0 ? Math.round((progress.sent + progress.failed) / progress.total * 100) : 0;

  return (
    <Card className={`mb-4 border ${done ? "border-[#34C759]/30 bg-[#34C759]/5" : isErr ? "border-[#FF3B30]/30 bg-[#FF3B30]/5" : "border-[#24A1DE]/30 bg-[#24A1DE]/5"}`} data-testid="bulk-dm-progress">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          {done ? <CheckCircle className="w-5 h-5 text-[#34C759] shrink-0" /> : isErr ? <AlertCircle className="w-5 h-5 text-[#FF3B30] shrink-0" /> : <Loader2 className="w-5 h-5 text-[#24A1DE] animate-spin shrink-0" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white">{done ? "Toplu DM tamamlandi!" : isErr ? "Hata olustu" : "DM gonderiliyor..."}</p>
            <p className="text-xs text-[#A1A1AA] mt-0.5 truncate">{done ? progress.message : isErr ? progress.status?.replace("error: ", "") : progress.current_user ? `@${progress.current_user}` : "Baslatiliyor..."}</p>
          </div>
          {done && <Button variant="ghost" size="sm" className="text-[#A1A1AA] hover:text-white h-7 px-2" onClick={onDismiss}>Kapat</Button>}
        </div>
        {!done && !isErr && (
          <>
            <Progress value={pct} className="h-1.5 bg-[#27272A] mb-2" />
            <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
              <span>%{pct}</span>
              <div className="flex gap-3">
                <span className="text-[#34C759]">{progress.sent || 0} gonderildi</span>
                {progress.failed > 0 && <span className="text-[#FF3B30]">{progress.failed} basarisiz</span>}
                <span>{progress.total || 0} toplam</span>
              </div>
            </div>
          </>
        )}
        {done && (
          <div className="flex gap-3 text-xs mt-1">
            <span className="text-[#34C759]">{progress.sent || 0} gonderildi</span>
            {progress.failed > 0 && <span className="text-[#FF3B30]">{progress.failed} basarisiz</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [channels, setChannels] = useState([]);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ content: "", target_type: "channel", target_id: "", parse_mode: "HTML" });
  const [bulkForm, setBulkForm] = useState({ content: "", source_filter: "all", target_channel_id: "", include_invite_link: false, parse_mode: "HTML" });
  const [sending, setSending] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bulk");
  const pollRef = useRef(null);

  useEffect(() => { loadData(); checkBulkStatus(); return () => clearInterval(pollRef.current); }, []);

  const loadData = async () => {
    try {
      const [msgsRes, chRes, srcRes] = await Promise.all([
        api.get("/messages"),
        api.get("/channels"),
        api.get("/users/sources")
      ]);
      setMessages(msgsRes.data.messages);
      setChannels(chRes.data.channels);
      setSources(srcRes.data.sources || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const checkBulkStatus = async () => {
    try {
      const { data } = await api.get("/messages/bulk-dm-status");
      if (data.running) {
        setBulkProgress(data);
        setBulkSending(true);
        startPolling();
      } else if (data.status === "completed") {
        setBulkProgress(data);
      }
    } catch {}
  };

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/messages/bulk-dm-status");
        setBulkProgress(data);
        if (!data.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBulkSending(false);
          loadData();
          if (data.status === "completed") toast.success("Toplu DM tamamlandi!");
        }
      } catch {}
    }, 3000);
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!form.target_id || !form.content) { toast.error("Hedef ve mesaj alanlari gerekli"); return; }
    setSending(true);
    try {
      await api.post("/messages/send", form);
      toast.success("Mesaj gonderildi");
      setForm({ ...form, content: "" });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Gonderim basarisiz"); }
    finally { setSending(false); }
  };

  const sendBulkDM = async () => {
    if (!bulkForm.content && !bulkForm.include_invite_link) { toast.error("Mesaj veya davet linki gerekli"); return; }
    if (bulkForm.include_invite_link && !bulkForm.target_channel_id) { toast.error("Davet linki icin hedef kanal secin"); return; }
    setBulkSending(true);
    try {
      const payload = {
        content: bulkForm.content,
        source_filter: bulkForm.source_filter === "all" ? null : bulkForm.source_filter,
        include_invite_link: bulkForm.include_invite_link,
        target_channel_id: bulkForm.include_invite_link ? bulkForm.target_channel_id : null,
        parse_mode: bulkForm.parse_mode
      };
      const { data } = await api.post("/messages/bulk-dm", payload);
      toast.info(data.message);
      if (data.invite_link) toast.info(`Davet linki: ${data.invite_link}`);
      setBulkProgress({ running: true, sent: 0, failed: 0, total: 0, status: "Baslatiliyor..." });
      startPolling();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toplu DM baslatilamadi");
      setBulkSending(false);
    }
  };

  const totalSourceCount = sources.reduce((a, s) => a + s.count, 0);
  const selectedSourceCount = bulkForm.source_filter === "all" ? totalSourceCount : (sources.find(s => s.name === bulkForm.source_filter)?.count || 0);

  return (
    <div data-testid="messages-page">
      <h1 className="font-heading text-3xl font-bold text-white mb-6 tracking-tight">Mesajlar</h1>

      {/* Bulk DM Progress */}
      <BulkDMProgress progress={bulkProgress} onDismiss={() => setBulkProgress(null)} />

      {/* Tabs */}
      <div className="flex gap-1 mb-6" data-testid="message-tabs">
        {[
          { key: "bulk", label: "Toplu DM", icon: Users },
          { key: "single", label: "Tekil Mesaj", icon: Send },
          { key: "history", label: "Gecmis", icon: MessageSquare }
        ].map(t => (
          <button
            key={t.key}
            data-testid={`msg-tab-${t.key}`}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              activeTab === t.key ? "bg-[#24A1DE] text-white" : "bg-[#1C1C1E] text-[#A1A1AA] hover:text-white hover:bg-[#2C2C2E]"
            }`}
          >
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {/* Bulk DM Tab */}
      {activeTab === "bulk" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="bg-[#121212] border-[#27272A] lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
                <Users className="w-5 h-5 text-[#24A1DE]" />Toplu DM Gonder
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Kaynak Filtresi */}
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Hedef Kitle</Label>
                <Select value={bulkForm.source_filter} onValueChange={(v) => setBulkForm({...bulkForm, source_filter: v})}>
                  <SelectTrigger data-testid="bulk-source-filter" className="bg-[#2C2C2E] border-transparent text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#27272A] max-h-[300px]">
                    <SelectItem value="all">Tum Kullanicilar ({totalSourceCount.toLocaleString("tr-TR")})</SelectItem>
                    {sources.map(s => (
                      <SelectItem key={s.name} value={s.name}>{s.name} ({s.count.toLocaleString("tr-TR")})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A1A1AA]">{selectedSourceCount.toLocaleString("tr-TR")} kullaniciya gonderilecek</p>
              </div>

              {/* Davet Linki */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
                <Checkbox
                  data-testid="include-invite-link"
                  checked={bulkForm.include_invite_link}
                  onCheckedChange={(v) => setBulkForm({...bulkForm, include_invite_link: v})}
                  className="border-[#A1A1AA]"
                />
                <div className="flex-1">
                  <p className="text-sm text-white">Davet linki ekle</p>
                  <p className="text-xs text-[#A1A1AA]">Mesajin sonuna hedef kanal/grup davet linki eklenir</p>
                </div>
                <Link2 className="w-4 h-4 text-[#24A1DE]" />
              </div>

              {bulkForm.include_invite_link && (
                <div className="space-y-1">
                  <Label className="text-[#A1A1AA] text-sm">Davet Linki Kanali</Label>
                  <Select value={bulkForm.target_channel_id} onValueChange={(v) => setBulkForm({...bulkForm, target_channel_id: v})}>
                    <SelectTrigger data-testid="bulk-invite-channel" className="bg-[#2C2C2E] border-transparent text-white">
                      <SelectValue placeholder="Kanal/Grup secin" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                      {channels.map(ch => (
                        <SelectItem key={ch.id} value={ch.id}>
                          <span className="flex items-center gap-2"><Hash className="w-3 h-3" />{ch.name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Mesaj Icerigi */}
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Mesaj Icerigi</Label>
                <Textarea
                  data-testid="bulk-dm-content"
                  value={bulkForm.content}
                  onChange={(e) => setBulkForm({...bulkForm, content: e.target.value})}
                  className="bg-[#2C2C2E] border-transparent text-white min-h-[120px] resize-none"
                  placeholder="Kullanicilara gonderilecek mesaji yazin..."
                />
              </div>

              {/* Format */}
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Format</Label>
                <Select value={bulkForm.parse_mode} onValueChange={(v) => setBulkForm({...bulkForm, parse_mode: v})}>
                  <SelectTrigger data-testid="bulk-format" className="bg-[#2C2C2E] border-transparent text-white w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                    <SelectItem value="HTML">HTML</SelectItem>
                    <SelectItem value="MarkdownV2">Markdown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                data-testid="send-bulk-dm-btn"
                onClick={sendBulkDM}
                disabled={bulkSending || (!bulkForm.content && !bulkForm.include_invite_link)}
                className="w-full bg-[#24A1DE] hover:bg-[#1A8BC7] text-white h-11"
              >
                {bulkSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {bulkSending ? "Gonderiliyor..." : `${selectedSourceCount.toLocaleString("tr-TR")} Kullaniciya DM Gonder`}
              </Button>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader className="pb-3">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
                <Radio className="w-5 h-5 text-[#FF9F0A]" />Bilgi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Toplam Alici</p>
                <p className="text-2xl font-bold text-[#24A1DE] font-heading">{totalSourceCount.toLocaleString("tr-TR")}</p>
              </div>
              <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
                <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Kaynak Kanal</p>
                <p className="text-lg font-bold text-white font-heading">{sources.length}</p>
              </div>
              <div className="space-y-1 text-xs text-[#A1A1AA]">
                <p>Telegram rate limit'lerine uygun gonderilir</p>
                <p>Her 3 mesajda operatör degistirilir</p>
                <p>Mesajlar arasi 2sn bekleme yapilir</p>
                <p>FloodWait durumunda otomatik beklenir</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Single Message Tab */}
      {activeTab === "single" && (
        <Card className="bg-[#121212] border-[#27272A] max-w-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
              <Send className="w-5 h-5 text-[#24A1DE]" />Tekil Mesaj Gonder
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={sendMessage} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Hedef Tur</Label>
                <Select value={form.target_type} onValueChange={(v) => setForm({...form, target_type: v, target_id: ""})}>
                  <SelectTrigger data-testid="message-target-type" className="bg-[#2C2C2E] border-transparent text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                    <SelectItem value="channel">Kanal</SelectItem>
                    <SelectItem value="user">Kullanici</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Hedef ID</Label>
                {form.target_type === "channel" && channels.length > 0 ? (
                  <Select value={form.target_id} onValueChange={(v) => setForm({...form, target_id: v})}>
                    <SelectTrigger data-testid="message-target-select" className="bg-[#2C2C2E] border-transparent text-white">
                      <SelectValue placeholder="Kanal secin" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1E] border-[#27272A]">
                      {channels.map(ch => (
                        <SelectItem key={ch.id} value={ch.telegram_channel_id}>{ch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid="message-target-id" value={form.target_id} onChange={(e) => setForm({...form, target_id: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white font-mono" placeholder="Telegram Chat ID" />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Mesaj</Label>
                <Textarea data-testid="message-content" value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white min-h-[120px] resize-none" placeholder="Mesajinizi yazin..." />
              </div>
              <Button data-testid="send-message-btn" type="submit" disabled={sending} className="w-full bg-[#24A1DE] hover:brightness-110 text-white">
                {sending ? "Gonderiliyor..." : "Gonder"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
              <MessageSquare className="w-5 h-5 text-[#24A1DE]" />Gonderim Gecmisi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length > 0 ? (
              <div className="space-y-3 max-h-[600px] overflow-auto pr-1">
                {messages.map(msg => (
                  <div key={msg.id} className="p-3 rounded-md bg-[#0A0A0A] border border-[#27272A]" data-testid={`message-item-${msg.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex gap-2">
                        <Badge variant="outline" className={`text-xs ${msg.target_type === "bulk_dm" ? "border-[#AF52DE] text-[#AF52DE]" : "border-[#24A1DE] text-[#24A1DE]"}`}>
                          {msg.target_type === "bulk_dm" ? "Toplu DM" : msg.target_type}
                        </Badge>
                        {msg.sent_count != null && (
                          <Badge variant="outline" className="border-[#34C759] text-[#34C759] text-xs">
                            {msg.sent_count}/{msg.total_count} gonderildi
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-[#A1A1AA]">{new Date(msg.sent_at).toLocaleString("tr-TR")}</span>
                    </div>
                    <p className="text-white text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    {msg.invite_link && (
                      <p className="text-xs text-[#24A1DE] mt-1 font-mono">{msg.invite_link}</p>
                    )}
                    {msg.target_type !== "bulk_dm" && (
                      <p className="text-xs text-[#A1A1AA] mt-1.5 font-mono">Hedef: {msg.target_id}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#A1A1AA] text-sm text-center py-10">Henuz mesaj gonderilmemis</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
